import './coupon-pocket.scss';

import type { FC } from 'react';

import { expiresWithinADay, expiryLabel, formatCouponValue, type IHeldCoupon } from '@/components/coupon/held-coupons';
import type { HeldCoupons } from '@/components/coupon/use-held-coupons';

interface CouponPocketProps {
	held: HeldCoupons;
	onUseNow: (coupon: IHeldCoupon) => void;
}

const Icon: FC<{ d: string }> = ({ d }) => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2.2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d={d} />
	</svg>
);

/**
 * Broadcast coupons waiting in the wallet popover, above the balance they'd be used on. Each is a
 * ticket stub: value on the left of a dashed tear, then code, expiry, message and the claim.
 */
const CouponPocket: FC<CouponPocketProps> = ({ held, onUseNow }) => {
	const { coupons, waiting, now, claims, claim, dismiss, undo, restore } = held;
	if (!coupons.length && !undo) return null;

	return (
		<section
			className='coupon-pocket'
			aria-label='Coupons for you'
		>
			<span className='wallet-nav__eyebrow wallet-nav__eyebrow--small'>
				{waiting ? `Coupons for you · ${waiting}` : 'Coupons'}
			</span>

			<ul className='coupon-pocket__list'>
				{coupons.map((coupon) => {
					const state = claims[coupon.couponId];
					const soon = !coupon.claimedAt && expiresWithinADay(coupon.expiresAt, now);
					const text = coupon.message || coupon.description;
					return (
						<li
							key={coupon.couponId}
							className={`coupon-pocket__ticket${soon ? ' coupon-pocket__ticket--soon' : ''}`}
						>
							<div className='coupon-pocket__value'>
								<b>{formatCouponValue(coupon)}</b>
								<small>on top-up</small>
							</div>

							<div className='coupon-pocket__body'>
								<div className='coupon-pocket__meta'>
									<span className='coupon-pocket__code'>{coupon.code}</span>
									<span className={`coupon-pocket__exp${soon ? ' coupon-pocket__exp--soon' : ''}`}>
										{expiryLabel(coupon.expiresAt, now)}
									</span>
								</div>
								{text && (
									<p
										className='coupon-pocket__msg'
										title={text}
									>
										{text}
									</p>
								)}

								<div className='coupon-pocket__acts'>
									{coupon.claimedAt ? (
										<>
											<span className='coupon-pocket__done'>
												<Icon d='M5 12l5 5 9-10' />
												In your wallet
											</span>
											<button
												type='button'
												className='coupon-pocket__btn'
												onClick={() => onUseNow(coupon)}
											>
												Use now →
											</button>
										</>
									) : (
										<>
											<button
												type='button'
												className='coupon-pocket__btn coupon-pocket__btn--claim'
												disabled={state?.status === 'claiming'}
												aria-busy={state?.status === 'claiming'}
												onClick={() => void claim(coupon)}
											>
												{state?.status === 'claiming'
													? 'Claiming…'
													: state?.status === 'failed'
														? 'Try again'
														: 'Claim to wallet'}
											</button>
											{state?.status === 'failed' && <span className='coupon-pocket__fail'>{state.message}</span>}
										</>
									)}
								</div>
							</div>

							<button
								type='button'
								className='coupon-pocket__x'
								aria-label={coupon.claimedAt ? `Close coupon ${coupon.code}` : `Dismiss coupon ${coupon.code}`}
								title={coupon.claimedAt ? 'Close' : 'Dismiss'}
								onClick={() => dismiss(coupon)}
							>
								<Icon d='M6 6l12 12M18 6L6 18' />
							</button>
						</li>
					);
				})}
			</ul>

			{undo && (
				<div
					className='coupon-pocket__undo'
					role='status'
				>
					<span>
						<b>{undo.code}</b> dismissed.
					</span>
					<button
						type='button'
						className='coupon-pocket__btn'
						onClick={restore}
					>
						Undo
					</button>
				</div>
			)}
		</section>
	);
};

export default CouponPocket;
